/**
 * Manpower P&L — Cost dashboards (READ-ONLY).
 *
 * One screen, two sections:
 *  1. City cost — City dropdown (useCities) + date + month inputs. Shows City Daily cost
 *     (base / OT / total cards) and City Monthly cost (week1-4 + est table).
 *  2. Facility cost — Facility dropdown (useFacilities) + date. Shows Facility Daily
 *     cost broken down by vendor in a table.
 *
 * All money fields arrive as decimal STRINGS — coerced with Number() and formatted ₹
 * with toLocaleString('en-IN'). No mutations; every section has loading/empty guards.
 */
import React, { useState } from 'react';
import { useCities } from '../../manpower-vendors/hooks/useCities';
import { useFacilities } from '../../manpower-workers/hooks/useFacilities';
import {
	useCityDailyCost,
	useCityMonthlyCost,
	useFacilityDailyCost,
} from '../hooks/useCostData';

/** Format a decimal-string (or number) money value as ₹ with Indian grouping. */
const formatMoney = (value?: string | number | null): string => {
	const n = Number(value ?? 0);
	if (!Number.isFinite(n)) return '₹0';
	return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** Today as YYYY-MM-DD, for the date input default. */
const todayISO = (): string => new Date().toISOString().slice(0, 10);
/** First day of the current month as YYYY-MM-DD, for the month input default. */
const monthStartISO = (): string => `${new Date().toISOString().slice(0, 7)}-01`;

const Card: React.FC<{ label: string; value: string; tone?: 'base' | 'ot' | 'total' }> = ({
	label,
	value,
	tone = 'base',
}) => {
	const tones = {
		base: 'border-gray-200 bg-white',
		ot: 'border-amber-200 bg-amber-50',
		total: 'border-green-200 bg-green-50',
	};
	return (
		// min-w so the three cards stack on phone instead of squeezing three-abreast
		<div className={`flex-1 min-w-[10rem] rounded-lg border p-4 ${tones[tone]}`}>
			<p className='text-xs font-medium uppercase tracking-wide text-gray-500'>{label}</p>
			<p className='mt-1 text-lg font-extrabold sm:font-semibold text-gray-900 tracking-tight tabular-nums'>{value}</p>
		</div>
	);
};

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<h2 className='mb-3 text-sm font-semibold text-gray-900'>{children}</h2>
);

export const CostDashboard: React.FC = () => {
	// City section controls
	const [cityId, setCityId] = useState<number | ''>('');
	const [cityDate, setCityDate] = useState<string>(todayISO());
	const [cityMonth, setCityMonth] = useState<string>(monthStartISO());

	// Facility section controls
	const [facilityId, setFacilityId] = useState<number | ''>('');
	const [facilityDate, setFacilityDate] = useState<string>(todayISO());

	const { data: cities = [] } = useCities();
	const { data: facilities = [] } = useFacilities();

	const cityDaily = useCityDailyCost(cityId, cityDate);
	const cityMonthly = useCityMonthlyCost(cityId, cityMonth);
	const facilityDaily = useFacilityDailyCost(facilityId, facilityDate);

	const inputCls =
		'w-full sm:w-auto rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none';
	const labelCls = 'text-xs font-medium text-gray-700';

	return (
		<div className='min-h-screen bg-white p-6'>
			<div className='mx-auto max-w-7xl'>
				<div className='mb-6'>
					<h1 className='text-xl font-semibold text-gray-900'>Cost</h1>
					<p className='text-sm text-gray-500'>
						Manpower P&amp;L — read-only cost dashboards by city and facility
					</p>
				</div>

				{/* ── City cost ─────────────────────────────────────────────── */}
				<div className='mb-8 rounded-lg border border-gray-200 p-5'>
					<SectionHeading>City cost</SectionHeading>

					<div className='mb-5 flex flex-wrap items-end gap-4'>
						<div className='flex w-full flex-col gap-1 sm:w-auto'>
							<label className={labelCls}>City</label>
							<select
								value={cityId}
								onChange={e => setCityId(e.target.value === '' ? '' : Number(e.target.value))}
								className={inputCls}
							>
								<option value=''>My city (default)</option>
								{cities.map(c => (
									<option key={c.id} value={c.id}>
										{c.name}
									</option>
								))}
							</select>
						</div>
						<div className='flex w-full flex-col gap-1 sm:w-auto'>
							<label className={labelCls}>Daily date</label>
							<input
								type='date'
								value={cityDate}
								onChange={e => setCityDate(e.target.value)}
								className={inputCls}
							/>
						</div>
						<div className='flex w-full flex-col gap-1 sm:w-auto'>
							<label className={labelCls}>Month</label>
							<input
								type='month'
								value={cityMonth.slice(0, 7)}
								onChange={e => setCityMonth(e.target.value ? `${e.target.value}-01` : '')}
								className={inputCls}
							/>
						</div>
					</div>

					{/* City daily — cards */}
					<div className='mb-6'>
						<p className='mb-2 text-xs font-medium uppercase tracking-wide text-gray-500'>
							Daily cost {cityDaily.data?.city_name ? `· ${cityDaily.data.city_name}` : ''}
						</p>
						{!cityDate ? (
							<p className='text-sm text-gray-500'>Pick a date to see daily cost.</p>
						) : cityDaily.isLoading ? (
							<p className='text-sm text-gray-500'>Loading…</p>
						) : cityDaily.error ? (
							<p className='text-sm text-red-600'>
								Failed to load: {(cityDaily.error as Error).message}
							</p>
						) : !cityDaily.data ? (
							<p className='text-sm text-gray-500'>No cost recorded for this date.</p>
						) : (
							<div className='flex flex-wrap gap-3'>
								<Card label='Base cost' value={formatMoney(cityDaily.data.total_base_cost)} tone='base' />
								<Card label='OT cost' value={formatMoney(cityDaily.data.total_ot_cost)} tone='ot' />
								<Card label='Total cost' value={formatMoney(cityDaily.data.total_cost)} tone='total' />
							</div>
						)}
					</div>

					{/* City monthly — week buckets + est */}
					<div>
						<p className='mb-2 text-xs font-medium uppercase tracking-wide text-gray-500'>
							Monthly cost {cityMonthly.data?.city_name ? `· ${cityMonthly.data.city_name}` : ''}
						</p>
						{!cityMonth ? (
							<p className='text-sm text-gray-500'>Pick a month to see the monthly breakdown.</p>
						) : cityMonthly.isLoading ? (
							<p className='text-sm text-gray-500'>Loading…</p>
						) : cityMonthly.error ? (
							<p className='text-sm text-red-600'>
								Failed to load: {(cityMonthly.error as Error).message}
							</p>
						) : !cityMonthly.data ? (
							<p className='text-sm text-gray-500'>No monthly cost recorded.</p>
						) : (
							<div className='overflow-x-auto rounded-lg border border-gray-200'>
								<table className='w-full border-collapse'>
									<thead>
										<tr className='border-b border-gray-200 bg-gray-50'>
											<th className='px-4 py-3 text-left text-xs font-bold text-gray-900'>Week 1</th>
											<th className='px-4 py-3 text-left text-xs font-bold text-gray-900'>Week 2</th>
											<th className='px-4 py-3 text-left text-xs font-bold text-gray-900'>Week 3</th>
											<th className='px-4 py-3 text-left text-xs font-bold text-gray-900'>Week 4</th>
											<th className='px-4 py-3 text-left text-xs font-bold text-gray-900'>Estimate</th>
										</tr>
									</thead>
									<tbody>
										<tr className='border-b border-gray-200'>
											<td className='px-4 py-3 text-xs text-gray-900'>{formatMoney(cityMonthly.data.week1)}</td>
											<td className='px-4 py-3 text-xs text-gray-900'>{formatMoney(cityMonthly.data.week2)}</td>
											<td className='px-4 py-3 text-xs text-gray-900'>{formatMoney(cityMonthly.data.week3)}</td>
											<td className='px-4 py-3 text-xs text-gray-900'>{formatMoney(cityMonthly.data.week4)}</td>
											<td className='px-4 py-3 text-xs font-semibold text-gray-900'>
												{formatMoney(cityMonthly.data.est)}
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						)}
					</div>
				</div>

				{/* ── Facility cost ─────────────────────────────────────────── */}
				<div className='rounded-lg border border-gray-200 p-5'>
					<SectionHeading>Facility cost by vendor</SectionHeading>

					<div className='mb-5 flex flex-wrap items-end gap-4'>
						<div className='flex w-full flex-col gap-1 sm:w-auto'>
							<label className={labelCls}>Facility</label>
							<select
								value={facilityId}
								onChange={e => setFacilityId(e.target.value === '' ? '' : Number(e.target.value))}
								className={inputCls}
							>
								<option value=''>Select a facility</option>
								{facilities.map(f => (
									<option key={f.id} value={f.id}>
										{f.location}
									</option>
								))}
							</select>
						</div>
						<div className='flex w-full flex-col gap-1 sm:w-auto'>
							<label className={labelCls}>Date</label>
							<input
								type='date'
								value={facilityDate}
								onChange={e => setFacilityDate(e.target.value)}
								className={inputCls}
							/>
						</div>
					</div>

					{facilityId === '' ? (
						<p className='text-sm text-gray-500'>Pick a facility to see its daily cost by vendor.</p>
					) : facilityDaily.isLoading ? (
						<p className='text-sm text-gray-500'>Loading…</p>
					) : facilityDaily.error ? (
						<p className='text-sm text-red-600'>
							Failed to load: {(facilityDaily.error as Error).message}
						</p>
					) : (facilityDaily.data ?? []).length === 0 ? (
						<p className='text-sm text-gray-500'>No vendor cost recorded for this facility and date.</p>
					) : (
						<div className='overflow-x-auto rounded-lg border border-gray-200'>
							<table className='w-full border-collapse'>
								<thead>
									<tr className='border-b border-gray-200 bg-gray-50'>
										<th className='px-4 py-3 text-left text-xs font-bold text-gray-900'>Vendor ID</th>
										<th className='px-4 py-3 text-right text-xs font-bold text-gray-900'>Base cost</th>
										<th className='px-4 py-3 text-right text-xs font-bold text-gray-900'>OT cost</th>
										<th className='px-4 py-3 text-right text-xs font-bold text-gray-900'>Total cost</th>
									</tr>
								</thead>
								<tbody>
									{(facilityDaily.data ?? []).map((row, i) => (
										<tr key={row.vendor_id ?? i} className='border-b border-gray-200 hover:bg-gray-50'>
											<td className='px-4 py-3 text-xs text-gray-900'>{row.vendor_id ?? '—'}</td>
											<td className='px-4 py-3 text-right text-xs text-gray-900'>
												{formatMoney(row.total_base_cost)}
											</td>
											<td className='px-4 py-3 text-right text-xs text-gray-900'>
												{formatMoney(row.total_ot_cost)}
											</td>
											<td className='px-4 py-3 text-right text-xs font-semibold text-gray-900'>
												{formatMoney(row.total_cost)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default CostDashboard;
